import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete, Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Grid, IconButton,
  MenuItem, Paper, Step, StepLabel, Stepper, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Typography, alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  useCreateCompanyMutation,
  useCreateSignageUserMutation,
  useUpdateSignageUserMutation,
  useCreateDeviceMutation,
  useUpdateDeviceMutation,
  useCreatePlaylistMutation,
  useUpdatePlaylistMutation,
  useCreatePlaylistGroupMutation,
  useUpdatePlaylistGroupMutation,
  useAddPlaylistToGroupMutation,
  useFinalizeOnboardingMutation,
  useGetPlaylistGroupsQuery,
  useGetPlaylistsQuery,
} from "../../../Api/attoDeskApi";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY, WARNING,
} from "../../../theme/tokens";

type SourceMode = "new" | "existing";

type GroupOption = {
  id: number;
  name: string;
  playback_mode: string;
  playlists_count?: number;
  is_active: boolean;
  company_id: number | null;
};

type PlaylistOption = {
  id: number;
  name: string;
  items_count?: number;
  is_active: boolean;
  company_id: number | null;
  playlist_group_id?: number | null;
  playlist_group?: { id: number; name: string } | null;
};

type WizardState = {
  // Step 1 — Company
  companyName: string;
  companyEmail: string;
  // Step 2 — First User
  userName: string;
  userUsername: string;
  userEmail: string;
  userPassword: string;
  userRole: "company_admin" | "content_manager";
  // Step 3 — Device
  deviceName: string;
  deviceCode: string;
  deviceLocation: string;
  // Once the user hand-edits the code we stop mirroring the device name.
  deviceCodeManuallyEdited: boolean;
  // Step 4 — Playlist Group
  groupMode: SourceMode;
  groupName: string;
  groupPlaybackMode: "SEQUENTIAL" | "LOOP_ALL" | "RANDOM";
  groupDescription: string;
  groupActive: boolean;
  selectedGroup: GroupOption | null;
  groupSkipped: boolean;
  // Step 5 — Playlist
  playlistMode: SourceMode;
  playlistName: string;
  playlistDescription: string;
  playlistActive: boolean;
  selectedPlaylist: PlaylistOption | null;
  playlistSkipped: boolean;
  // Real DB ids persisted as we progress. `companyId`, `firstUserId` and
  // `deviceId` drive Create-vs-Update mode for their steps. `createdGroupId`
  // / `createdPlaylistId` track only the records THIS wizard created (in the
  // "new" sub-mode) so revisiting updates that record instead of creating a
  // second one — selecting an existing record never touches these.
  companyId: number | null;
  firstUserId: number | null;
  deviceId: number | null;
  deviceCodeCreated: string | null;
  createdGroupId: number | null;
  createdPlaylistId: number | null;
  playlistGroupId: number | null;
  playlistId: number | null;
};

const initialState: WizardState = {
  companyName: "", companyEmail: "",
  userName: "", userUsername: "", userEmail: "", userPassword: "",
  userRole: "company_admin",
  deviceName: "", deviceCode: "", deviceLocation: "", deviceCodeManuallyEdited: false,
  groupMode: "new", groupName: "", groupPlaybackMode: "SEQUENTIAL",
  groupDescription: "", groupActive: true, selectedGroup: null, groupSkipped: false,
  playlistMode: "new", playlistName: "", playlistDescription: "",
  playlistActive: true, selectedPlaylist: null, playlistSkipped: false,
  companyId: null, firstUserId: null, deviceId: null, deviceCodeCreated: null,
  createdGroupId: null, createdPlaylistId: null,
  playlistGroupId: null, playlistId: null,
};

// A non-empty email must look like an address; an empty value is valid
// because the company/user email is optional.
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

// Device code = device name, lowercased, stripped to [a-z0-9] only.
//   "Lobby Screen 01" → "lobbyscreen01"   "VM Device #1" → "vmdevice1"
const normalizeDeviceCode = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, "");

const PLAYBACK_LABEL: Record<string, string> = {
  SEQUENTIAL: "Sequential", LOOP_ALL: "Loop All", RANDOM: "Random",
};

const STEPS = [
  "Company",
  "First User",
  "Device",
  "Playlist Group",
  "Playlist",
  "Assign + Launch",
];

// Persisted so an interrupted onboarding can resume with its real record ids
// intact. A saved session is NEVER auto-loaded into the live form — the user
// is asked to Resume or Start New (see the session gate in the component) so a
// stale company_id can't silently flip Step 1 into Update mode for a brand-new
// client. Each session is namespaced by its own id and status, and the
// password is NEVER written to storage.
const STORAGE_KEY = "vm_onboarding_wizard_v1";

type OnboardingStatus = "in_progress" | "completed" | "cancelled";

type PersistedSession = {
  sessionId: string;
  status: OnboardingStatus;
  step: number;
  state: WizardState;
};

const newSessionId = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  return `ob_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
};

const loadPersisted = (): PersistedSession | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const state: WizardState = {
      ...initialState,
      ...(parsed.state || {}),
      // Never restore a password from storage — resumed users always start
      // with a blank (optional) password field.
      userPassword: "",
    };
    const maxStep = STEPS.length - 1;
    const step = typeof parsed.step === "number"
      ? Math.max(0, Math.min(parsed.step, maxStep))
      : 0;
    const status: OnboardingStatus =
      parsed.status === "completed" || parsed.status === "cancelled"
        ? parsed.status : "in_progress";
    const sessionId = typeof parsed.sessionId === "string" && parsed.sessionId
      ? parsed.sessionId : newSessionId();
    return { sessionId, status, step, state };
  } catch {
    return null;
  }
};

const savePersisted = (p: PersistedSession) => {
  try {
    const { userPassword, ...rest } = p.state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      sessionId: p.sessionId,
      status: p.status,
      step: p.step,
      state: { ...rest, userPassword: "" },
    }));
  } catch { /* storage unavailable — progress simply isn't resumable */ }
};

const clearPersisted = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

// A saved session is worth resuming only if it actually holds progress — a
// real record id or at least an entered company name. Blank auto-saved
// sessions (and completed/cancelled ones) never trigger the resume prompt.
const hasResumableProgress = (p: PersistedSession | null): boolean => {
  if (!p || p.status !== "in_progress") return false;
  const s = p.state;
  return !!(
    s.companyId || s.firstUserId || s.deviceId ||
    s.createdGroupId || s.createdPlaylistId ||
    s.playlistGroupId || s.playlistId ||
    s.companyName.trim()
  );
};

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const { showMessage, showErrorMessage } = useNotifier();

  // Restore any saved progress exactly once (real DB ids + current step +
  // non-sensitive form values).
  // ── Onboarding session gate ──────────────────────────────────────
  // A new session ALWAYS starts blank in Create mode; a previous session is
  // only ever adopted through an explicit "Resume" choice. This is the fix
  // for a stale company_id silently opening Step 1 in Update mode.
  const [sessionReady, setSessionReady] = useState(false);
  const [resumeCandidate, setResumeCandidate] = useState<PersistedSession | null>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [sessionStatus, setSessionStatus] = useState<OnboardingStatus>("in_progress");
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>(initialState);
  const set = <K extends keyof WizardState>(key: K, v: WizardState[K]) =>
    setState((s) => ({ ...s, [key]: v }));

  // Distinguishes a brand-new blank session (safe to scrub autofill) from a
  // deliberately resumed one (whose values must be preserved).
  const freshSession = useRef(true);

  const beginNewSession = () => {
    setSessionId(newSessionId());
    setSessionStatus("in_progress");
    setState(initialState);   // every record id back to null → Create mode
    setStep(0);
    setResumeCandidate(null);
    freshSession.current = true;
    setSessionReady(true);
  };

  const resumeSavedSession = () => {
    const saved = resumeCandidate;
    if (!saved) return;
    setSessionId(saved.sessionId);
    setSessionStatus("in_progress");
    setState({ ...saved.state, userPassword: "" });
    setStep(saved.step);
    setResumeCandidate(null);
    freshSession.current = false;
    setSessionReady(true);
  };

  const discardAndStartNew = () => {
    // Confirm before throwing away unfinished progress. Only the onboarding
    // draft is discarded — records already created are left untouched.
    const ok = window.confirm(
      "Discard the unfinished onboarding progress and start a new client? " +
      "Any records already created will not be deleted.",
    );
    if (!ok) return;
    clearPersisted();
    beginNewSession();
  };

  // Decide once on mount: show the Resume/Start-New choice only when a saved
  // session actually holds progress; otherwise drop any blank/completed
  // leftover and begin a fresh blank session.
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    const saved = loadPersisted();
    if (hasResumableProgress(saved)) {
      setResumeCandidate(saved);   // await the user's choice; don't persist yet
    } else {
      clearPersisted();
      beginNewSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [createCompany, { isLoading: isCreatingCo }] = useCreateCompanyMutation();
  const [createUser, { isLoading: isCreatingUser }] = useCreateSignageUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateSignageUserMutation();
  const [createDevice, { isLoading: isCreatingDevice }] = useCreateDeviceMutation();
  const [updateDevice, { isLoading: isUpdatingDevice }] = useUpdateDeviceMutation();
  const [createPlaylist, { isLoading: isCreatingPlaylist }] = useCreatePlaylistMutation();
  const [updatePlaylist, { isLoading: isUpdatingPlaylist }] = useUpdatePlaylistMutation();
  const [createGroup, { isLoading: isCreatingGroup }] = useCreatePlaylistGroupMutation();
  const [updateGroup, { isLoading: isUpdatingGroup }] = useUpdatePlaylistGroupMutation();
  const [addPlaylistToGroup] = useAddPlaylistToGroupMutation();
  const [finalizeOnboarding, { isLoading: isAssigning }] = useFinalizeOnboardingMutation();

  // Persist progress on every change once the session is resolved. Password is
  // stripped so no plain-text secret is written; a completed session is
  // cleared instead of saved so the next visit starts fresh. Nothing is
  // written while the Resume/Start-New choice is still pending, so the saved
  // session can't be clobbered before the user decides.
  useEffect(() => {
    if (!sessionReady) return;
    if (sessionStatus === "completed") { clearPersisted(); return; }
    savePersisted({ sessionId, status: "in_progress", step, state });
  }, [state, step, sessionReady, sessionId, sessionStatus]);

  // One-time scrub of any credentials a password manager injected into the
  // First User step before the user has typed anything. It only runs for a
  // fresh session (never a resumed one) and only in Create mode, so it can
  // never wipe values the user typed or a resumed onboarding restored.
  const autofillScrubbed = useRef(false);
  useEffect(() => {
    if (!sessionReady || step !== 1 || autofillScrubbed.current) return;
    if (!freshSession.current) return;                 // resumed session keeps its values
    if (state.firstUserId) return;                     // editing an existing user
    autofillScrubbed.current = true;
    const t = setTimeout(() => {
      setState((s) => (s.firstUserId
        ? s
        : { ...s, userUsername: "", userPassword: "" }));
    }, 0);
    return () => clearTimeout(t);
  }, [sessionReady, step, state.firstUserId]);

  // Existing-record lists, scoped to the company created in step 1. Skipped
  // until a company exists so we never fetch across companies.
  const { data: groupsData } = useGetPlaylistGroupsQuery(
    state.companyId ? { company_id: state.companyId } : undefined,
    { skip: !state.companyId },
  );
  const { data: playlistsData } = useGetPlaylistsQuery(
    state.companyId ? { company_id: state.companyId } : undefined,
    { skip: !state.companyId },
  );

  const groupOptions: GroupOption[] = ((groupsData?.data as any[]) ?? []).map((g) => ({
    id: g.id, name: g.name, playback_mode: g.playback_mode,
    playlists_count: g.playlists_count, is_active: !!g.is_active, company_id: g.company_id,
  }));
  const playlistOptions: PlaylistOption[] = ((playlistsData?.data as any[]) ?? []).map((p) => ({
    id: p.id, name: p.name, items_count: p.items_count, is_active: !!p.is_active,
    company_id: p.company_id, playlist_group_id: p.playlist_group_id,
    playlist_group: p.playlist_group,
  }));

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          Onboarding is visible to Super Admins only.
        </Typography>
      </Box>
    );
  }

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  /* ─── Step submissions ─────────────────────────────────────────── */

  const submitCompany = async () => {
    if (!state.companyName.trim()) {
      showErrorMessage("Company name is required"); return;
    }
    if (state.companyEmail.trim() && !isValidEmail(state.companyEmail)) {
      showErrorMessage("Enter a valid company email or leave it blank"); return;
    }
    const isUpdate = !!state.companyId;
    const FAIL = isUpdate
      ? "The company could not be updated. Your onboarding progress has been preserved."
      : "The company could not be created. Please review the entered information.";
    try {
      // The /company endpoint upserts on `id`: presence ⇒ update, absence ⇒
      // create. We never re-create a company that already has an id.
      const res = await createCompany({
        ...(isUpdate ? { id: state.companyId } : {}),
        companyName: state.companyName.trim(),
        companyEmail: state.companyEmail.trim() || undefined,
      }).unwrap();
      if (res.status) {
        const id = (res.data as any)?.id ?? state.companyId;
        set("companyId", id);
        showMessage(`Company "${state.companyName}" ${isUpdate ? "updated" : "created"}`);
        next();
      } else {
        showErrorMessage(res.message || FAIL);
      }
    } catch (e: any) { showErrorMessage(e?.data?.message || FAIL); }
  };

  const submitUser = async () => {
    const name = state.userName.trim();
    const username = state.userUsername.trim();
    const isUpdate = !!state.firstUserId;
    if (!state.companyId) {
      showErrorMessage("Create a company first"); return;
    }
    if (!name || !username) {
      showErrorMessage("Full name and username are required"); return;
    }
    if (!isUpdate && !state.userPassword) {
      showErrorMessage("A password is required to create the user"); return;
    }
    const CREATE_FAIL = "The user could not be created. Please review the entered information.";
    const UPDATE_FAIL = "The user could not be updated. Your onboarding progress has been preserved.";
    try {
      if (isUpdate) {
        // Update the existing user by id. Company ownership is intentionally
        // NOT sent so it can never drift off the Step-1 company. Password is
        // sent only when a new, non-empty one was typed.
        const body: any = {
          name,
          username,
          email: state.userEmail.trim() || null,
          role: state.userRole,
        };
        if (state.userPassword) body.password = state.userPassword;
        const res = await updateUser({ id: state.firstUserId as number, body }).unwrap();
        if (res.status) {
          set("userPassword", "");
          showMessage("User updated");
          next();
        } else {
          showErrorMessage(res.message || UPDATE_FAIL);
        }
      } else {
        const res = await createUser({
          name,
          username,
          email: state.userEmail.trim() || null,
          password: state.userPassword,
          role: state.userRole,
          company_id: state.companyId,
          is_active: true,
        }).unwrap();
        if (res.status) {
          set("firstUserId", (res.data as any)?.id);
          set("userPassword", "");
          showMessage("User created");
          next();
        } else {
          showErrorMessage(res.message || CREATE_FAIL);
        }
      }
    } catch (e: any) {
      // The onboarding user was deleted outside the wizard: don't silently
      // spawn a duplicate — drop the stale id so the button reverts to
      // "Create User" and the admin recreates it intentionally.
      if (isUpdate && (e?.status === 404 || e?.originalStatus === 404 || e?.data?.code === 404)) {
        set("firstUserId", null);
        showErrorMessage(
          "The previously created user no longer exists. Review the details and click Create User to create a replacement.",
        );
        return;
      }
      showErrorMessage(e?.data?.message || (isUpdate ? UPDATE_FAIL : CREATE_FAIL));
    }
  };

  const submitDevice = async () => {
    const name = state.deviceName.trim();
    const code = normalizeDeviceCode(state.deviceCode);
    const isUpdate = !!state.deviceId;
    if (!name) { showErrorMessage("Device name is required"); return; }
    if (!code) { showErrorMessage("Device code is required."); return; }
    if (!state.companyId) {
      showErrorMessage("Create a company first"); return;
    }
    const CREATE_FAIL = "The device could not be created. Please review the entered information.";
    const UPDATE_FAIL = "The device could not be updated. Your onboarding progress has been preserved.";
    try {
      if (isUpdate) {
        // Update the existing device by id. company_id / playlist_group_id /
        // playlist_id / uuid / heartbeat fields are deliberately not sent, so
        // the server preserves the company assignment, any group assignment
        // and every device-generated field.
        const res = await updateDevice({
          id: state.deviceId as number,
          body: {
            device_name: name,
            device_code: code,
            location: state.deviceLocation.trim() || null,
          },
        }).unwrap();
        if (res.status) {
          const d = res.data as any;
          set("deviceCodeCreated", d?.device_code ?? code);
          showMessage("Device updated");
          next();
        } else {
          showErrorMessage(res.message || UPDATE_FAIL);
        }
      } else {
        const res = await createDevice({
          device_name: name,
          device_code: code,
          // Onboarding always sends an explicit code — enforce it server-side.
          require_device_code: true,
          location: state.deviceLocation.trim() || null,
          company_id: state.companyId,
        }).unwrap();
        if (res.status) {
          const d = res.data as any;
          set("deviceId", d?.id);
          set("deviceCodeCreated", d?.device_code);
          showMessage(`Device "${d?.device_code}" created`);
          next();
        } else {
          showErrorMessage(res.message || CREATE_FAIL);
        }
      }
    } catch (e: any) {
      // Device deleted outside the wizard: drop the stale id so the button
      // reverts to "Create Device" and the admin recreates it intentionally,
      // rather than being stuck retrying an update that can never succeed.
      if (isUpdate && (e?.status === 404 || e?.originalStatus === 404 || e?.data?.code === 404)) {
        set("deviceId", null);
        set("deviceCodeCreated", null);
        showErrorMessage(
          "The previously created device no longer exists. Review the details and click Create Device to create a replacement.",
        );
        return;
      }
      showErrorMessage(e?.data?.message || (isUpdate ? UPDATE_FAIL : CREATE_FAIL));
    }
  };

  const submitGroup = async () => {
    if (!state.companyId) { showErrorMessage("Create a company first"); return; }

    if (state.groupMode === "existing") {
      if (!state.selectedGroup) {
        showErrorMessage("Select a playlist group or choose Create New"); return;
      }
      if (state.selectedGroup.company_id !== null && state.selectedGroup.company_id !== state.companyId) {
        showErrorMessage("That playlist group belongs to a different company"); return;
      }
      // Store the real DB id; selecting an existing group creates/updates
      // nothing — leave createdGroupId untouched.
      set("playlistGroupId", state.selectedGroup.id);
      set("groupSkipped", false);
      next();
      return;
    }

    // Create New / Update the group this wizard created.
    if (!state.groupName.trim()) {
      showErrorMessage("Group name is required"); return;
    }
    const isUpdate = !!state.createdGroupId;
    const FAIL = isUpdate
      ? "The playlist group could not be updated. Your onboarding progress has been preserved."
      : "The playlist group could not be created. Please review the entered information.";
    const body: any = {
      name: state.groupName.trim(),
      playback_mode: state.groupPlaybackMode,
      description: state.groupDescription.trim() || null,
      is_active: state.groupActive,
    };
    try {
      if (isUpdate) {
        const res = await updateGroup({ id: state.createdGroupId as number, body }).unwrap();
        if (!res.status) { showErrorMessage(res.message || FAIL); return; }
        set("playlistGroupId", state.createdGroupId);
        set("selectedGroup", {
          id: state.createdGroupId as number, name: body.name,
          playback_mode: state.groupPlaybackMode, is_active: state.groupActive,
          company_id: state.companyId,
        });
        set("groupSkipped", false);
        showMessage("Playlist group updated");
        next();
      } else {
        const res = await createGroup({ ...body, company_id: state.companyId }).unwrap();
        if (!res.status) { showErrorMessage(res.message || FAIL); return; }
        const gid = (res.data as any)?.id;
        set("createdGroupId", gid);
        set("playlistGroupId", gid);
        set("selectedGroup", {
          id: gid, name: body.name,
          playback_mode: state.groupPlaybackMode, is_active: state.groupActive,
          company_id: state.companyId,
        });
        set("groupSkipped", false);
        showMessage("Playlist group created");
        next();
      }
    } catch (e: any) { showErrorMessage(e?.data?.message || FAIL); }
  };

  const skipGroup = () => {
    set("playlistGroupId", null);
    set("selectedGroup", null);
    set("groupSkipped", true);
    next();
  };

  // Attach a playlist to the selected group unless it is already a member.
  const ensurePlaylistInGroup = async (playlistId: number, alreadyGroupId?: number | null) => {
    if (!state.playlistGroupId) return;
    if (alreadyGroupId && alreadyGroupId === state.playlistGroupId) return; // no duplicate
    await addPlaylistToGroup({ id: state.playlistGroupId, playlist_id: playlistId }).unwrap();
  };

  const submitPlaylist = async () => {
    if (!state.companyId) { showErrorMessage("Create a company first"); return; }

    if (state.playlistMode === "existing") {
      const sel = state.selectedPlaylist;
      if (!sel) {
        showErrorMessage("Select a playlist or choose Create New"); return;
      }
      if (sel.company_id !== null && sel.company_id !== state.companyId) {
        showErrorMessage("That playlist belongs to a different company"); return;
      }
      try {
        await ensurePlaylistInGroup(sel.id, sel.playlist_group_id);
        set("playlistId", sel.id);
        set("playlistSkipped", false);
        if (state.playlistGroupId) showMessage("Playlist linked to group");
        next();
      } catch (e: any) { showErrorMessage(e?.data?.message || "Could not link playlist"); }
      return;
    }

    // Create New / Update the playlist this wizard created — an empty playlist
    // is allowed; media is added later.
    if (!state.playlistName.trim()) {
      showErrorMessage("Playlist name is required"); return;
    }
    const isUpdate = !!state.createdPlaylistId;
    const FAIL = isUpdate
      ? "The playlist could not be updated. Your onboarding progress has been preserved."
      : "The playlist could not be created. Please review the entered information.";
    const body: any = {
      name: state.playlistName.trim(),
      description: state.playlistDescription.trim() || null,
      is_active: state.playlistActive,
    };
    try {
      if (isUpdate) {
        const res = await updatePlaylist({ id: state.createdPlaylistId as number, body }).unwrap();
        if (!res.status) { showErrorMessage(res.message || FAIL); return; }
        const pid = state.createdPlaylistId as number;
        await ensurePlaylistInGroup(pid, null);
        set("playlistId", pid);
        set("selectedPlaylist", {
          id: pid, name: body.name, is_active: state.playlistActive,
          company_id: state.companyId,
        });
        set("playlistSkipped", false);
        showMessage("Playlist updated");
        next();
      } else {
        const res = await createPlaylist({ ...body, company_id: state.companyId }).unwrap();
        if (!res.status) { showErrorMessage(res.message || FAIL); return; }
        const pid = (res.data as any)?.id;
        await ensurePlaylistInGroup(pid, null);
        set("createdPlaylistId", pid);
        set("playlistId", pid);
        set("selectedPlaylist", {
          id: pid, name: body.name, is_active: state.playlistActive,
          company_id: state.companyId,
        });
        set("playlistSkipped", false);
        showMessage("Playlist created");
        next();
      }
    } catch (e: any) { showErrorMessage(e?.data?.message || FAIL); }
  };

  const skipPlaylist = () => {
    set("playlistId", null);
    set("selectedPlaylist", null);
    set("playlistSkipped", true);
    next();
  };

  const finalize = async () => {
    // One authoritative, transactional call to the backend. It links the
    // playlist to the group AND assigns the group to the device, then returns
    // the hydrated device. We only report success once we have verified the
    // saved playlist_group_id in the response — never on optimistic frontend
    // state. On any failure onboarding progress is preserved for a safe retry.
    if (!state.deviceId) {
      showErrorMessage("No device to assign to"); return false;
    }
    const FAIL_MSG =
      "The playlist group could not be assigned to the device. " +
      "Your onboarding progress has been preserved. Please try again.";
    try {
      const res = await finalizeOnboarding({
        id: state.deviceId,
        playlist_group_id: state.playlistGroupId ?? null,
        playlist_id: state.playlistId ?? null,
      }).unwrap();

      if (!res.status) {
        showErrorMessage(res.message || FAIL_MSG);
        return false;
      }

      // Verify persistence: the device returned by the API must carry the
      // group we asked for. Compare as numbers to avoid "1" !== 1 mismatches.
      if (state.playlistGroupId) {
        const savedGroupId = (res.data as any)?.playlist_group_id ?? null;
        if (Number(savedGroupId) !== Number(state.playlistGroupId)) {
          showErrorMessage(FAIL_MSG);
          return false;
        }
      }

      // Onboarding finished — mark the session completed and clear the
      // resumable draft so the next visit starts fresh in Create mode
      // instead of resuming a completed client.
      setSessionStatus("completed");
      clearPersisted();

      showMessage("Onboarding complete");
      return true;
    } catch (e: any) {
      showErrorMessage(e?.data?.message || FAIL_MSG);
      return false;
    }
  };

  /* ─── Render ───────────────────────────────────────────────────── */

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.18)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
          color: "#7c3aed",
        }}>
          <RocketLaunchIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            Onboard New Client
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            Guided setup — every step is reversible and most are skippable.
          </Typography>
        </Box>
      </Box>

      {!sessionReady ? (
        resumeCandidate ? (
          <ResumeChoice
            saved={resumeCandidate}
            onResume={resumeSavedSession}
            onStartNew={discardAndStartNew} />
        ) : (
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${BORDER}`, p: 6, mb: 3,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 2,
          }}>
            <CircularProgress size={20} sx={{ color: GOLD }} />
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
              Preparing onboarding…
            </Typography>
          </Paper>
        )
      ) : (
        <>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, p: 3, mb: 3 }}>
            <Stepper activeStep={step} alternativeLabel>
              {STEPS.map((label, i) => (
                <Step key={label} completed={i < step}>
                  <StepLabel>
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{label}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, p: 4, mb: 3 }}>
            {step === 0 && (
              <StepCompany state={state} set={set} onSubmit={submitCompany} isLoading={isCreatingCo} />
            )}
            {step === 1 && (
              <StepUser state={state} set={set} onSubmit={submitUser}
                isLoading={isCreatingUser || isUpdatingUser} />
            )}
            {step === 2 && (
              <StepDevice state={state} set={set} onSubmit={submitDevice}
                isLoading={isCreatingDevice || isUpdatingDevice} />
            )}
            {step === 3 && (
              <StepGroup
                state={state} set={set} options={groupOptions}
                onSubmit={submitGroup} onSkip={skipGroup}
                isLoading={isCreatingGroup || isUpdatingGroup} />
            )}
            {step === 4 && (
              <StepPlaylist
                state={state} set={set} options={playlistOptions}
                onSubmit={submitPlaylist} onSkip={skipPlaylist}
                isLoading={isCreatingPlaylist || isUpdatingPlaylist} />
            )}
            {step === 5 && (
              <StepFinal state={state} onFinalize={finalize} isAssigning={isAssigning} navigate={navigate} />
            )}
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={back} disabled={step === 0}
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: "none", fontWeight: 600 }}>
              Back
            </Button>
            <Button onClick={() => navigate("/dashboard")}
              sx={{ textTransform: "none", color: TEXT_SECONDARY }}>
              Exit (progress saved on each step)
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

/* ─── Step components ───────────────────────────────────────────── */

const ResumeChoice: React.FC<{
  saved: PersistedSession;
  onResume: () => void;
  onStartNew: () => void;
}> = ({ saved, onResume, onStartNew }) => {
  const s = saved.state;
  const stepLabel = STEPS[saved.step] ?? STEPS[0];
  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, p: 4, mb: 3 }}>
      <SectionHeader
        title="Resume unfinished onboarding?"
        hint="You have onboarding progress that wasn't completed. Resume it, or start a brand-new client." />
      <Box sx={{
        p: 2, mb: 3, borderRadius: 2,
        background: alpha(GOLD, 0.06), border: `1px solid ${alpha(GOLD, 0.3)}`,
      }}>
        <SummaryRow label="Company" value={s.companyName || "— (not yet created)"} />
        <SummaryRow label="Resumes at" value={`Step ${saved.step + 1} — ${stepLabel}`} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button variant="outlined" onClick={onStartNew}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}>
          Start New Onboarding
        </Button>
        <PrimaryButton onClick={onResume}>Resume Previous Onboarding</PrimaryButton>
      </Box>
    </Paper>
  );
};

const StepCompany: React.FC<{
  state: WizardState; set: any; onSubmit: () => void; isLoading: boolean;
}> = ({ state, set, onSubmit, isLoading }) => {
  const isUpdate = !!state.companyId;
  return (
    <Box>
      <SectionHeader title="Step 1 — Create Company" />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Company name *" value={state.companyName}
            onChange={(e) => set("companyName", e.target.value)} size="small" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Company email" type="email" value={state.companyEmail}
            onChange={(e) => set("companyEmail", e.target.value)} size="small"
            error={!!state.companyEmail.trim() && !isValidEmail(state.companyEmail)}
            helperText={
              !!state.companyEmail.trim() && !isValidEmail(state.companyEmail)
                ? "Enter a valid email address"
                : undefined
            } />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <PrimaryButton onClick={onSubmit} loading={isLoading}>
          {isUpdate ? "Update Company" : "Create Company"}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

const StepUser: React.FC<{
  state: WizardState; set: any; onSubmit: () => void; isLoading: boolean;
}> = ({ state, set, onSubmit, isLoading }) => {
  const isUpdate = !!state.firstUserId;
  // Fields render read-only until first focus so browser / password-manager
  // autofill can't inject the logged-in Super Admin's credentials. A user
  // click unlocks the field for normal typing.
  const [unlockUsername, setUnlockUsername] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState(false);
  return (
    // A real <form> with autoComplete off, and onboarding-specific field
    // names/ids that don't match the login form, so nothing is prefilled.
    <Box component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
      <SectionHeader title={isUpdate ? "Step 2 — Update First User" : "Step 2 — Create First User"}
        hint="This user signs in to the admin to manage their company's content." />
      {isUpdate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Editing the saved user — changes update the existing account. Leave the
          password blank to keep the current password.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Full name *" value={state.userName}
            onChange={(e) => set("userName", e.target.value)} size="small"
            id="onboarding-client-fullname"
            inputProps={{ name: "onboarding-client-fullname", autoComplete: "name" }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Username *" value={state.userUsername}
            onChange={(e) => set("userUsername", e.target.value)} size="small"
            id="onboarding-client-login"
            onFocus={() => setUnlockUsername(true)}
            InputProps={{ readOnly: !unlockUsername }}
            inputProps={{ name: "onboarding-client-login", autoComplete: "off" }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth type="email" label="Email" value={state.userEmail}
            onChange={(e) => set("userEmail", e.target.value)} size="small"
            id="onboarding-client-contact-email"
            inputProps={{ name: "onboarding-client-contact-email", autoComplete: "email" }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth type="password"
            label={isUpdate ? "New Password (optional)" : "Password *"}
            value={state.userPassword}
            onChange={(e) => set("userPassword", e.target.value)} size="small"
            id="onboarding-client-secret"
            onFocus={() => setUnlockPassword(true)}
            InputProps={{ readOnly: !unlockPassword }}
            inputProps={{ name: "onboarding-client-secret", autoComplete: "new-password" }}
            helperText={isUpdate ? "Leave blank to keep the existing password." : undefined} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField select fullWidth label="Role" value={state.userRole}
            onChange={(e) => set("userRole", e.target.value)} size="small">
            <MenuItem value="company_admin">Company Admin</MenuItem>
            <MenuItem value="content_manager">Content Manager</MenuItem>
          </TextField>
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <PrimaryButton onClick={onSubmit} loading={isLoading}>
          {isUpdate ? "Update User" : "Create User"}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

const StepDevice: React.FC<{
  state: WizardState; set: any; onSubmit: () => void; isLoading: boolean;
}> = ({ state, set, onSubmit, isLoading }) => {
  const isUpdate = !!state.deviceId;
  const codeMissing = !state.deviceCode.trim();
  const nameMissing = !state.deviceName.trim();

  const onNameChange = (raw: string) => {
    set("deviceName", raw);
    // The code mirrors the name until the user hand-edits it.
    if (!state.deviceCodeManuallyEdited) set("deviceCode", normalizeDeviceCode(raw));
  };
  const onCodeChange = (raw: string) => {
    set("deviceCode", normalizeDeviceCode(raw));
    set("deviceCodeManuallyEdited", true);
  };
  const regenerateCode = () => {
    set("deviceCode", normalizeDeviceCode(state.deviceName));
    set("deviceCodeManuallyEdited", false);
  };

  return (
    <Box>
      <SectionHeader title={isUpdate ? "Step 3 — Update Device" : "Step 3 — Create Device"}
        hint="The device code is generated from the device name and is what the Android player asks for at setup." />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Device name *" value={state.deviceName}
            onChange={(e) => onNameChange(e.target.value)} size="small"
            error={nameMissing}
            helperText={nameMissing ? "Device name is required." : undefined} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Device code *" value={state.deviceCode}
            onChange={(e) => onCodeChange(e.target.value)} size="small"
            placeholder="Device code *"
            error={codeMissing}
            helperText={codeMissing
              ? "Device code is required."
              : (state.deviceCodeManuallyEdited ? "Manually edited." : "Auto-generated from device name.")} />
          {state.deviceCodeManuallyEdited && (
            <Button onClick={regenerateCode} startIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
              sx={{ mt: 0.5, textTransform: "none", fontWeight: 600, fontSize: 12, color: TEXT_SECONDARY }}>
              Regenerate from device name
            </Button>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Location" value={state.deviceLocation}
            onChange={(e) => set("deviceLocation", e.target.value)} size="small" />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <PrimaryButton onClick={onSubmit} loading={isLoading}
          disabled={nameMissing || codeMissing}>
          {isUpdate ? "Update Device" : "Create Device"}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

const StepGroup: React.FC<{
  state: WizardState; set: any; options: GroupOption[];
  onSubmit: () => void; onSkip: () => void; isLoading: boolean;
}> = ({ state, set, options, onSubmit, onSkip, isLoading }) => {
  const inactiveSelected = state.groupMode === "existing"
    && state.selectedGroup && !state.selectedGroup.is_active;
  const isUpdate = state.groupMode === "new" && !!state.createdGroupId;
  return (
    <Box>
      <SectionHeader title="Step 4 — Playlist Group"
        hint="A group holds one or more playlists in playback order. The device plays the group, not raw playlists." />

      <ModeToggle
        value={state.groupMode}
        onChange={(m) => set("groupMode", m)}
        newLabel="Create New Playlist Group"
        existingLabel="Use Existing Playlist Group" />

      <CompanyLock name={state.companyName} />

      {state.groupMode === "new" ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Group name *" value={state.groupName}
              onChange={(e) => set("groupName", e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Playback mode" value={state.groupPlaybackMode}
              onChange={(e) => set("groupPlaybackMode", e.target.value)} size="small">
              <MenuItem value="SEQUENTIAL">Sequential</MenuItem>
              <MenuItem value="LOOP_ALL">Loop All</MenuItem>
              <MenuItem value="RANDOM">Random</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" value={state.groupDescription}
              onChange={(e) => set("groupDescription", e.target.value)} size="small"
              multiline minRows={2} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={state.groupActive}
                onChange={(e) => set("groupActive", e.target.checked)} />}
              label="Active" />
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Autocomplete
            options={options}
            value={state.selectedGroup}
            onChange={(_, v) => set("selectedGroup", v)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Search playlist groups"
                placeholder="Type to search…" />
            )}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{o.name}</Typography>
                    {!o.is_active && <Chip size="small" color="warning" label="Inactive" />}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                    {PLAYBACK_LABEL[o.playback_mode] || o.playback_mode}
                    {" · "}{o.playlists_count ?? 0} playlist(s)
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText="No playlist groups for this company yet" />
          <AddNewLink label="+ Add New Playlist Group"
            onClick={() => set("groupMode", "new")} />
          {inactiveSelected && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This playlist group is <b>inactive</b>. Selecting it will not activate it —
              the device may show no content until the group is enabled.
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onSkip} sx={{ textTransform: "none", color: TEXT_SECONDARY }}>
          Skip — no content yet
        </Button>
        <PrimaryButton onClick={onSubmit} loading={isLoading}>
          {state.groupMode === "new"
            ? (isUpdate ? "Update Playlist Group" : "Create Playlist Group")
            : "Use Selected Group"}
        </PrimaryButton>
      </Box>
      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1, textAlign: "right" }}>
        Skipping leaves the device without an assigned playlist group.
      </Typography>
    </Box>
  );
};

const StepPlaylist: React.FC<{
  state: WizardState; set: any; options: PlaylistOption[];
  onSubmit: () => void; onSkip: () => void; isLoading: boolean;
}> = ({ state, set, options, onSubmit, onSkip, isLoading }) => {
  const groupName = state.selectedGroup?.name;
  const isUpdate = state.playlistMode === "new" && !!state.createdPlaylistId;
  return (
    <Box>
      <SectionHeader title="Step 5 — Playlist"
        hint="Add or pick a playlist for this client. Media can be added later from the Playlist page — an empty playlist is fine." />

      <ModeToggle
        value={state.playlistMode}
        onChange={(m) => set("playlistMode", m)}
        newLabel="Create New Playlist"
        existingLabel="Use Existing Playlist" />

      <CompanyLock name={state.companyName} />

      {groupName ? (
        <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 2 }}>
          Will be linked to playlist group: <b>{groupName}</b>
        </Typography>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          No playlist group selected — the playlist will be created but not linked to a group.
        </Alert>
      )}

      {state.playlistMode === "new" ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Playlist name *" value={state.playlistName}
              onChange={(e) => set("playlistName", e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel sx={{ mt: 0.5 }}
              control={<Switch checked={state.playlistActive}
                onChange={(e) => set("playlistActive", e.target.checked)} />}
              label="Active" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" value={state.playlistDescription}
              onChange={(e) => set("playlistDescription", e.target.value)} size="small"
              multiline minRows={2} />
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Autocomplete
            options={options}
            value={state.selectedPlaylist}
            onChange={(_, v) => set("selectedPlaylist", v)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Search playlists"
                placeholder="Type to search…" />
            )}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{o.name}</Typography>
                    {!o.is_active && <Chip size="small" color="warning" label="Inactive" />}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                    {o.items_count ?? 0} media item(s)
                    {o.playlist_group?.name ? ` · in group: ${o.playlist_group.name}` : ""}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText="No playlists for this company yet" />
          <AddNewLink label="+ Add New Playlist"
            onClick={() => set("playlistMode", "new")} />
        </Box>
      )}

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onSkip} sx={{ textTransform: "none", color: TEXT_SECONDARY }}>
          Skip
        </Button>
        <PrimaryButton onClick={onSubmit} loading={isLoading}>
          {state.playlistMode === "new"
            ? (isUpdate ? "Update Playlist" : "Create Playlist")
            : "Use Selected Playlist"}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

const StepFinal: React.FC<{
  state: WizardState; onFinalize: () => Promise<boolean>; isAssigning: boolean;
  navigate: (path: string) => void;
}> = ({ state, onFinalize, isAssigning, navigate }) => {
  const playerUrl = useMemo(() => {
    if (!state.deviceCodeCreated) return null;
    const base = window.location.origin;
    return `${base}/player/${state.deviceCodeCreated}`;
  }, [state.deviceCodeCreated]);

  const [completed, setCompleted] = useState(false);
  const triggerFinalize = async () => {
    const ok = await onFinalize();
    if (!ok) return;
    setCompleted(true);
    // Onboarding is complete and every assignment is persisted — send the
    // admin straight to the new device's View page, which loads the saved
    // playlist-group assignment through the normal Device API (not router
    // state). Uses the real created device id captured in wizard state.
    if (state.deviceId) {
      navigate(`/device/view/${state.deviceId}`);
    }
  };

  const copy = (text: string) => navigator.clipboard.writeText(text);

  const groupName = state.selectedGroup?.name || (state.groupSkipped ? "— (skipped)" : "—");
  const playlistName = state.selectedPlaylist?.name || (state.playlistSkipped ? "— (skipped)" : "—");

  return (
    <Box>
      <SectionHeader
        title="Step 6 — Assign + Launch"
        hint="Review the summary, then assign the playlist group to the device and launch." />

      {!completed && (
        <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
          <PrimaryButton onClick={triggerFinalize} loading={isAssigning}>
            {state.playlistGroupId ? "Assign Group → Device & Finish" : "Finish"}
          </PrimaryButton>
        </Box>
      )}

      <Paper elevation={0} sx={{
        p: 3, borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(GOLD, 0.06)} 0%, ${alpha(GOLD, 0.02)} 100%)`,
        border: `1px solid ${alpha(GOLD, 0.3)}`,
        mb: 3,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CheckCircleIcon sx={{ color: completed ? "#10b981" : TEXT_SECONDARY }} />
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>
            {completed ? "Onboarding Complete" : "Setup Summary"}
          </Typography>
        </Box>
        <SummaryRow label="Company" value={state.companyName || "—"} />
        <SummaryRow label="First User" value={state.userUsername || "—"} />
        <SummaryRow label="Device Name" value={state.deviceName || "—"} />
        <SummaryRow label="Device Code"
          value={state.deviceCodeCreated || "—"}
          copyable={!!state.deviceCodeCreated} onCopy={copy} />
        <SummaryRow label="Playlist Group" value={groupName} />
        <SummaryRow label="Playlist" value={playlistName} />
        {playerUrl && (
          <SummaryRow label="Web Player URL"
            value={playerUrl} copyable onCopy={copy}
            extra={
              <IconButton size="small" onClick={() => window.open(playerUrl, "_blank")}
                sx={{ ml: 1 }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            } />
        )}
      </Paper>

      {completed && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
          {state.deviceId && (
            <Button variant="outlined" onClick={() => navigate(`/device/view/${state.deviceId}`)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
              View Device
            </Button>
          )}
          {state.playlistGroupId && (
            <Button variant="outlined"
              onClick={() => navigate(`/playlist-groups/edit/${state.playlistGroupId}`)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
              View Playlist Group
            </Button>
          )}
          {state.playlistId && (
            <Button variant="outlined"
              onClick={() => navigate(`/playlist/edit/${state.playlistId}`)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
              Edit Playlist
            </Button>
          )}
          {state.deviceId && (
            <Button variant="outlined"
              onClick={() => navigate(`/devices/${state.deviceId}/monitoring`)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
              Open Device Monitoring
            </Button>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button variant="outlined" onClick={() => navigate("/installation-guide")}
          sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
          Open Installation Guide
        </Button>
        <Box sx={{ flex: 1 }} />
        {/* Done must never bypass assignment. Before completion it runs the
            same authoritative finalize (which redirects to the device on
            success); after completion it goes to the new device's View page,
            not the Dashboard. */}
        <PrimaryButton
          onClick={
            completed
              ? () => navigate(state.deviceId ? `/device/view/${state.deviceId}` : "/dashboard")
              : triggerFinalize
          }
          loading={isAssigning}
        >
          Done
        </PrimaryButton>
      </Box>
    </Box>
  );
};

/* ─── Shared UI ─────────────────────────────────────────────────── */

const ModeToggle: React.FC<{
  value: SourceMode; onChange: (m: SourceMode) => void;
  newLabel: string; existingLabel: string;
}> = ({ value, onChange, newLabel, existingLabel }) => (
  <ToggleButtonGroup
    exclusive value={value} size="small"
    onChange={(_, v) => v && onChange(v)}
    sx={{
      mb: 2,
      "& .MuiToggleButton-root": {
        textTransform: "none", fontWeight: 700, fontSize: 12.5, px: 2.5,
      },
      "& .Mui-selected": {
        background: `${alpha(GOLD, 0.15)} !important`,
        color: `${NAVY} !important`,
      },
    }}>
    <ToggleButton value="new">{newLabel}</ToggleButton>
    <ToggleButton value="existing">{existingLabel}</ToggleButton>
  </ToggleButtonGroup>
);

const CompanyLock: React.FC<{ name: string }> = ({ name }) => (
  <Box sx={{ mb: 2 }}>
    <TextField fullWidth size="small" label="Company" value={name || "—"}
      InputProps={{ readOnly: true }} disabled
      helperText="Company is locked to the client created in Step 1." />
  </Box>
);

const AddNewLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Button onClick={onClick} startIcon={<AddIcon />}
    sx={{ mt: 1, textTransform: "none", fontWeight: 700, color: WARNING }}>
    {label}
  </Button>
);

const SectionHeader: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontSize: 16, fontWeight: 800, color: NAVY, mb: 0.5 }}>
      {title}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>{hint}</Typography>
    )}
  </Box>
);

const SummaryRow: React.FC<{
  label: string; value: string;
  copyable?: boolean; onCopy?: (v: string) => void; extra?: React.ReactNode;
}> = ({ label, value, copyable, onCopy, extra }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 2, py: 1,
    borderBottom: `1px solid ${alpha(GOLD, 0.15)}`,
    "&:last-child": { borderBottom: "none" },
  }}>
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
      textTransform: "uppercase", letterSpacing: "0.06em",
      width: 200, flexShrink: 0,
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY,
      fontFamily: copyable ? "monospace" : undefined,
      flex: 1, wordBreak: "break-all",
    }}>
      {value}
    </Typography>
    {copyable && onCopy && (
      <IconButton size="small" onClick={() => onCopy(value)}>
        <ContentCopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    )}
    {extra}
  </Box>
);

const PrimaryButton: React.FC<{
  onClick: () => void; loading?: boolean; disabled?: boolean; children: React.ReactNode;
}> = ({ onClick, loading, disabled, children }) => (
  <Button variant="contained" disableElevation onClick={onClick} disabled={loading || disabled}
    endIcon={<ArrowForwardIcon />}
    sx={{
      textTransform: "none", borderRadius: 2, fontWeight: 700, px: 3,
      background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
      color: "#0b1e2d",
      "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
      "&.Mui-disabled": { background: alpha(GOLD, 0.4), color: alpha("#0b1e2d", 0.5) },
    }}>
    {loading ? "Working…" : children}
  </Button>
);

export default Onboarding;
